/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { useState, MouseEvent, useEffect, ReactNode, FC } from 'react'
import { DropdownItem, IDropdownOption } from 'components/forms/Dropdown'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useToggle } from 'utils/useToggle'

/**
 * A hook containing some state and a click handler that are often used along
 * with the OverflowMenu component.
 *
 * Returns the expanded state and setExpanded state update function as well as
 * a toggle function which includes a call to `e.stopPropagation`, ensuring
 * that the OverflowMenu's trigger is the only thing clicked.
 */
export const useOverflowToggle = (initialState?: boolean) => {
  const [expanded, setExpanded] = useState(initialState ?? false)
  const clickOverflowTrigger = (e: MouseEvent<{}>) => {
    e.stopPropagation()
    setExpanded(currExpanded => !currExpanded)
  }

  return { expanded, setExpanded, clickOverflowTrigger }
}

export interface IOverflowMenuProps {
  /**
   * Each option should contain at the very least a label and action. Label can
   * either be text or a react component.
   *
   * NOTE: Do not use `label` as the text of the option anymore, prefer to use
   * `itemComponent` to pass a component that will be rendered as the option.
   */
  options: IDropdownOption[]
  expanded: boolean

  /**
   * This is used as an HTML id and to generate a key prop for each dropdown
   * item.
   */
  dropdownId: string
  dataTest?: string

  /**
   * A component that, when clicked, opens the overflow menu.
   */
  trigger: ReactNode
  onSelectMenuItem?: (optionIdx: number) => void

  /*
   * Set to right will position the dropdown part of the overflow component
   * with its right most edge flush with the right side of its container.
   */
  alignment?: 'left' | 'right'

  /*
   * Callback invoked when the menu's `expanded` internal state changes
   */
  onExpanded: (isOpen: boolean) => void

  className?: string
}

export const OverflowMenu: FC<IOverflowMenuProps> = ({
  alignment,
  className,
  dataTest = 'dropdown',
  dropdownId,
  expanded,
  onExpanded,
  onSelectMenuItem,
  options,
  trigger,
}) => {
  const { isMobile } = useBreakpointContext()
  const [open, toggle, wrapperRef] = useToggle<HTMLDivElement>(expanded)

  useEffect(() => {
    // Ensures that the external `expanded` state always matches the
    // internal toggle state.
    if (expanded !== open) {
      toggle()
    }
  }, [expanded])

  useEffect(() => {
    // If the overflow menu is closed by clicking outside of the dropdown (i.e.
    // changing the internal toggle state), then update the external open
    // state.
    if (!open) {
      onExpanded(open)
    }
  }, [open])

  const selectMenuItemHandler = (optionIdx: number) => {
    toggle()
    options[optionIdx].action()
    if (onSelectMenuItem) onSelectMenuItem(optionIdx)
  }

  const closeHandler = () => toggle()

  return (
    <div className={`${className} dropdown overflow-menu`} ref={wrapperRef}>
      {trigger}
      {isMobile && open && (
        <div className='overflow-menu-background' onClick={closeHandler} onKeyDown={closeHandler} />
      )}
      <div role='listbox' className={`dropdown-listbox ${alignment} ${expanded ? '' : 'dn'}`}>
        {options.map((option, i) => (
          <DropdownItem
            key={`${dropdownId}-${option.label}`}
            itemComponent={option.itemComponent}
            label={option.label}
            id={option.id}
            disabled={option.disabled}
            dataTest={dataTest}
            index={i}
            onClick={selectMenuItemHandler}
          />
        ))}
      </div>
    </div>
  )
}

export default OverflowMenu
